/* eslint-disable react/no-danger */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import s from '../../../../../redux/selectors';
import { wrap, withT } from '../../../../../lib/wrappers';

import BlankLink from '../../../../common/BlankLink';
import InlineInput from '../InlineInput';
import InlineSelect from '../InlineSelect';
import types from '../../../../../types';

const fuelOptions = t => [
  { value: 0, display: t('natural gas') },
  { value: 1, display: t('electricity') },
  { value: 2, display: t('fuel oil') },
];

const heatingPercentOptions = [
  { value: '3', display: '300 %' },
  { value: '2.5', display: '250 %' },
  { value: '2', display: '200 %' },
  { value: '1.8', display: '180 %' },
  { value: '1.6', display: '160 %' },
  { value: '1.4', display: '140 %' },
  { value: '1.2', display: '120 %' },
  { value: '1', display: '100 %' },
  { value: '0.8', display: '80 %' },
  { value: '0.6', display: '60 %' },
  { value: '0.4', display: '40 %' },
  { value: '0.2', display: '20 %' },
];

const fuelUnits = (inputTakeactionThermostatWinterFueltype, isMetric, t) => {
  switch (inputTakeactionThermostatWinterFueltype) {
    case 0: {
      return `${isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`;
    }
    case 1: {
      return t('kWh', { ns: 'units' });
    }
    case 2: {
      return isMetric ? t('liters', { ns: 'units' }) : t('gallons', { ns: 'units' });
    }
    default: {
      throw new Error(`Unexpected winter fuel type ${inputTakeactionThermostatWinterFueltype}.`);
    }
  }
};

const TurnDownThermostatInWinter = ({
  t,
  isMetric,
  widgetSource,
  inputFootprintHousingSquarefeet,
  inputTakeactionThermostatWinterFueltype,
  resultTakeactionThermostatWinterDollarstypical,
  resultTakeactionThermostatWinterDollarsuse,
  resultTakeactionThermostatWinterIntensity,
  resultTakeactionThermostatWinterGCO2fueltype,
  resultTakeactionThermostatWinterPu,
}) => (
  <div className="action-details">
    <div className="action-details__form">
      <p>
        {t('I heat my home with')}
        <InlineSelect
          options={fuelOptions(t)}
          apiKey={'input_takeaction_thermostat_winter_fueltype'}
        />
        <span
          dangerouslySetInnerHTML={{
            // eslint-disable-next-line no-template-curly-in-string
            __html: t('Typical {{homeArea}} {{homeAreaUnits}} homes in my climate spend ${{resultTakeactionThermostatWinterDollarstypical}} per year on heating. I heat my home about', {
              homeArea: inputFootprintHousingSquarefeet.ensuredDisplay,
              homeAreaUnits: `${t((isMetric ? 'm' : 'ft'), { ns: 'units' })}<sup>2</sup>`,
              resultTakeactionThermostatWinterDollarstypical,
            }),
          }}
        />
        <InlineSelect
          options={heatingPercentOptions}
          apiKey={'input_takeaction_thermostat_winter_percentuse'}
        />
        {
          // eslint-disable-next-line no-template-curly-in-string
          t('of typical households, or ${{resultTakeactionThermostatWinterDollarsuse}} per year. I will turn my thermostat down', {
            resultTakeactionThermostatWinterDollarsuse,
          })
        }
        <InlineInput
          maxLength={4}
          apiKey={'input_takeaction_thermostat_winter_daydegrees'}
        />
        {t('degree {{scale}} on winter days and', { scale: isMetric ? t('C', { ns: 'units' }) : t('F', { ns: 'units' }) })}
        <InlineInput
          maxLength={4}
          apiKey={'input_takeaction_thermostat_winter_nightdegrees'}
        />
        {t('degree {{scale}} on winter nights.', { scale: isMetric ? t('C', { ns: 'units' }) : t('F', { ns: 'units' }) })}
      </p>
    </div>
    <div className="action-details__facts">
      {t('Smart settings on your thermostat is one of the best things you can do to save energy because heating is the largest single contributor to energy use in typical homes, even in many moderate climates.')}
    </div>
    <div className="action-details__links">
      {widgetSource !== 'ontario' && <BlankLink
        href="http://www.coolcalifornia.org/funding-wizard#search//%255BZipcode%253D%253D94720%255D%255BEligible+Applicants%253D%253DIndividual%255D%255BCategory%253D%253DEnergy%255D/false/none/false/1/12"
        text={t('Find rebates and incentives for this action')}
      />}
      {widgetSource === 'ontario' &&
        <p>
          {t('You can get $100 towards a smart thermostat, which adapts to your ideal energy patterns, boosting your overall energy-awareness while offering comfort and savings you can rely on. To learn more about this program, check out')}
          &nbsp;
          <BlankLink href={`https://${t('greenon.ca/')}`} text="GreenOn.ca" />.
        </p>
      }
    </div>
    <div className="action-details__assumptions">
      <div className="action-details__assumptions__header">{t('Assumptions')}</div>
      <ul>
        <li>
          {t('cost of electricity')}
          <InlineInput
            apiKey="input_takeaction_assumption_kwhprice"
            maxLength={3}
          />
          {t('cents per kWh')}
        </li>
        <li>
          {t('Price of natural gas')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_assumption_ngprice"
          />
          <span
          // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: t('cents/{{ngUnit}} (based on your location)', {
                ngUnit: isMetric ? `${t('m', { ns: 'units' })}<sup>3</sup>` : t('therm', { ns: 'units' }),
              }),
            }}
          />
        </li>
        <li>
          {t('Price of fuel oil')}
          <InlineInput
            maxLength={3}
            apiKey="input_takeaction_thermostat_winter_fueloilprice"
          />
          {t('cents/{{unitVolume}}', { unitVolume: isMetric ? t('L', { ns: 'units' }) : t('gal', { ns: 'units' }) })}
        </li>
        <li>
          {t('Heating degree days')}
          <InlineInput
            maxLength={4}
            apiKey="input_takeaction_thermostat_winter_hdd"
          />
        </li>
        <li>
          {t('Number of hours with temperature set for')}
          <ul>
            <li>
              {t('Weekdays during the day')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_winter_wdsetup"
              />
            </li>
            <li>
              {t('Weekdays at night')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_winter_wdntsetup"
              />
            </li>
            <li>
              {t('Weekends during the day')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_winter_wesetup"
              />
            </li>
            <li>
              {t('Weekends at night')}
              <InlineInput
                maxLength={2}
                apiKey="input_takeaction_thermostat_winter_wentsetup"
              />
            </li>
          </ul>
        </li>
        <li
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionThermostatWinterIntensity}} {{fuelUnits}}{{units}} consumption intensity', {
              fuelUnits:
                fuelUnits(inputTakeactionThermostatWinterFueltype.ensured, isMetric, t),
              resultTakeactionThermostatWinterIntensity,
              units: `*1000 / (${t('HDD')}*${(isMetric ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' }))}<sup>2</sup>/1000)`,
            }),
          }}
        />
        <li
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionThermostatWinterGCO2fueltype}} gCO<sub>2</sub>eq emitted per {{fuelUnits}} of fuel', {
              resultTakeactionThermostatWinterGCO2fueltype,
              fuelUnits:
                fuelUnits(inputTakeactionThermostatWinterFueltype.ensured, isMetric, t),
            }),
          }}
        />
        <li
          dangerouslySetInnerHTML={{
            __html: t('{{resultTakeactionThermostatWinterPu}} annual {{fuelUnits}} consumed', {
              resultTakeactionThermostatWinterPu,
              fuelUnits:
                fuelUnits(inputTakeactionThermostatWinterFueltype.ensured, isMetric, t),
            }),
          }}
        />
      </ul>
    </div>
  </div>
);

TurnDownThermostatInWinter.propTypes = {
  // wrapped
  t: PropTypes.func.isRequired,
  // connected props
  isMetric: PropTypes.bool.isRequired,
  widgetSource: PropTypes.string.isRequired,
  inputFootprintHousingSquarefeet: types.UserInput.isRequired,
  inputTakeactionThermostatWinterFueltype: types.UserInput.isRequired,
  resultTakeactionThermostatWinterDollarstypical: PropTypes.string.isRequired,
  resultTakeactionThermostatWinterDollarsuse: PropTypes.string.isRequired,
  resultTakeactionThermostatWinterIntensity: PropTypes.string.isRequired,
  resultTakeactionThermostatWinterGCO2fueltype: PropTypes.string.isRequired,
  resultTakeactionThermostatWinterPu: PropTypes.string.isRequired,
};

const mapStateToProps = (state, ownProps) => [
  'resultTakeactionThermostatWinterDollarstypical',
  'resultTakeactionThermostatWinterDollarsuse',
  'resultTakeactionThermostatWinterIntensity',
  'resultTakeactionThermostatWinterPu',
].reduce((resultProps, resultKey) => ({
  ...resultProps,
  [resultKey]: s.results(resultKey)(state, ownProps),
}), {
  resultTakeactionThermostatWinterGCO2fueltype:
    s.results('result_takeaction_thermostat_winter_gCO2fueltype', true)(state, ownProps),
  isMetric: s.isMetric(state),
  widgetSource: s.widgetSource(state),
  inputFootprintHousingSquarefeet:
    s.inputFootprintHousingSquarefeet(state, ownProps),
  inputTakeactionThermostatWinterFueltype:
    s.inputTakeactionThermostatWinterFueltype(state, ownProps),
});

const mapDispatchToProps = () => ({});

export default wrap(
  withT('thermostatWinter'),
  connect(mapStateToProps, mapDispatchToProps)(TurnDownThermostatInWinter),
);
